import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { Dialog, DialogProps, Slide, useMediaQuery, useTheme } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

/**
 * Slide up transition for mobile dialogs
 */
const SlideUpTransition = forwardRef<unknown, TransitionProps & { children: React.ReactElement }>(
  function SlideUpTransition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  }
);

/**
 * Hook to track keyboard height using visualViewport API
 * Returns the keyboard height (difference between window height and visual viewport height)
 */
const useKeyboardHeight = (isActive: boolean): number => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const initialViewportHeight = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      setKeyboardHeight(0);
      return;
    }

    const visualViewport = window.visualViewport;
    if (!visualViewport) {
      return;
    }

    // Store initial viewport height on first activation
    if (initialViewportHeight.current === null) {
      initialViewportHeight.current = visualViewport.height;
    }

    const handleResize = () => {
      // Calculate keyboard height as the difference between window inner height and visual viewport height
      const windowHeight = window.innerHeight;
      const viewportHeight = visualViewport.height;
      const offset = visualViewport.offsetTop;
      
      // The keyboard height is the difference between the window height and the visual viewport
      const calculatedKeyboardHeight = Math.max(0, windowHeight - viewportHeight - offset);
      
      // Only set keyboard height if it's significant (> 100px to avoid false positives)
      if (calculatedKeyboardHeight > 100) {
        setKeyboardHeight(calculatedKeyboardHeight);
      } else {
        setKeyboardHeight(0);
      }
    };

    // Initial check
    handleResize();

    visualViewport.addEventListener("resize", handleResize);
    visualViewport.addEventListener("scroll", handleResize);

    return () => {
      visualViewport.removeEventListener("resize", handleResize);
      visualViewport.removeEventListener("scroll", handleResize);
    };
  }, [isActive]);

  return keyboardHeight;
};

export interface MobileSlideDialogProps extends DialogProps {
  /**
   * If true, the dialog will slide up from bottom on mobile and be fullScreen
   * Default: true on mobile (< md breakpoint)
   */
  mobileFullScreen?: boolean;
  /**
   * If true, the dialog will automatically adjust its position when the keyboard appears
   * Default: true
   */
  keyboardAware?: boolean;
}

/**
 * MobileSlideDialog - A Dialog wrapper that slides up from the bottom on mobile devices
 * 
 * On mobile (< md breakpoint):
 * - Dialog slides up from bottom
 * - Takes full screen
 * - Has rounded top corners
 * - Automatically adjusts when keyboard appears (keyboardAware)
 * 
 * On desktop (>= md breakpoint):
 * - Standard centered dialog behavior
 */
const MobileSlideDialog: React.FC<MobileSlideDialogProps> = ({
  children,
  mobileFullScreen = true,
  keyboardAware = true,
  keepMounted = true,
  sx,
  PaperProps,
  open,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const shouldSlideUp = mobileFullScreen && isMobile;
  
  // Track keyboard height for keyboard avoidance
  const keyboardHeight = useKeyboardHeight(Boolean(open) && shouldSlideUp && keyboardAware);

  return (
    <Dialog data-testid="ui-mobile-slide-dialog-modal-action"
      {...props}
      open={open}
      fullScreen={shouldSlideUp}
      TransitionComponent={shouldSlideUp ? SlideUpTransition : undefined}
      keepMounted={keepMounted}
      PaperProps={{
        ...PaperProps,
        sx: {
          ...(shouldSlideUp && {
            // Mobile sheet styling - slide from bottom with rounded top corners
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            // Adjust for keyboard visibility
            maxHeight: keyboardHeight > 0 ? `calc(95vh - ${keyboardHeight}px)` : '95vh',
            marginTop: 'auto',
            marginBottom: keyboardHeight > 0 ? keyboardHeight : 0,
            transition: 'max-height 0.15s ease-out, margin-bottom 0.15s ease-out',
          }),
          ...PaperProps?.sx,
        },
      }}
      sx={{
        ...(shouldSlideUp && {
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
          },
        }),
        ...sx,
      }}
    >
      {children}
    </Dialog>
  );
};

export default MobileSlideDialog;
export { SlideUpTransition, useKeyboardHeight };


